import { useFormik } from 'formik';
import React, { useEffect, useMemo } from 'react';
import { IoClose } from 'react-icons/io5';
import { ActionIcon, Button, FileInput, Modal, Select, Switch, Text, Textarea } from 'rizzui';
import { toast } from 'sonner';
import * as yup from 'yup';
import ContractTemplateWYSIWYGEditor from '../../../components/contract-templates/ContractTemplateWYSIWYGEditor';
import InvoiceConceptBillingSection from '../../../components/invoice-concepts/InvoiceConceptBillingSection';
import RequiredInput from '../../../components/forms/InputHOC';
import FormGroup from '../../../layout/shared/form-group';
import { DocumentsService } from '../../../services/document/documentsService';
import { ProductService } from '../../../services/product/productService';
import { ProductTypeService } from '../../../services/product-type/productTypeService';
import { convertEditorJSONToHTML, convertHTMLToEditorJSON, isValidHTML } from '../../../utils/editorConversions';
import { formatValidationError, PRODUCT_TYPE_FIELD_LABELS } from '../../../utils/validationErrorLabels';

interface ProductTypeFormData {
    id?: string;
    name: string;
    description: string;
    renewalSettings: string | null;
    renewalDaysNumber: number | null;
    contractTemplate: string | null;
    consultancyFieldsRequired: boolean;
    templateConceptClientInvoice: string;
    templateConceptTutorInvoice: string;
    templateConceptInternalInvoice: string;
}

interface ProductTypeModalFormProps {
    data?: any;
    isOpen: boolean;
    onClose: () => void;
    refetch: () => void;
}

export const renewalSettingsOptions = [
    { label: 'Sin configuración', value: '' },
    { label: 'Al principio del año siguiente', value: 'next_year_beginning' },
    { label: 'Año natural', value: 'natural_year' },
    { label: 'Periodo Personalizado', value: 'custom_period' },
];

const schema = yup.object({
    name: yup.string().min(1, 'Demasiado corto').max(300, 'Demasiado largo').required('Campo obligatorio'),
    description: yup.string().nullable(),
    renewalSettings: yup.string().nullable(),
    renewalDaysNumber: yup.number().nullable().when('renewalSettings', {
        is: 'custom_period',
        then: (schema) => schema.required('El número de días es requerido para configuración personalizada').min(1, 'Debe ser mayor a 0'),
        otherwise: (schema) => schema.nullable(),
    }),
    contractTemplate: yup.string().nullable().notRequired(),
    consultancyFieldsRequired: yup.boolean().notRequired(),
    templateConceptClientInvoice: yup.string().nullable().notRequired(),
    templateConceptTutorInvoice: yup.string().nullable().notRequired(),
    templateConceptInternalInvoice: yup.string().nullable().notRequired(),
});

const ProductTypeModalForm: React.FC<ProductTypeModalFormProps> = ({ data, isOpen, onClose, refetch }) => {

    const productTypeService = new ProductTypeService();
    const mode = data?.productTypeId ? 'Editar' : 'Crear';
    const [variables, setVariables] = React.useState<any[]>([]);
    const [contractWatermarkFile, setContractWatermarkFile] = React.useState<File | null>(null);
    const [contractWatermarkPreviewUrl, setContractWatermarkPreviewUrl] = React.useState<string | null>(null);
    const [removeContractWatermark, setRemoveContractWatermark] = React.useState(false);
    const productTypeInputId = data?.productTypeId + mode;

    const parseBooleanValue = (value: any): boolean => [true, 1, '1', 'true', 't'].includes(value);
    const loadDocumentPreview = async (documentId: string) => {
        try {
            const response = await (await (new DocumentsService()).renderDocument(documentId)).getResponse();
            const file = new Blob([response.data], { type: response.headers?.['content-type'] || 'image/*' });
            const objectUrl = URL.createObjectURL(file);
            setContractWatermarkPreviewUrl(objectUrl);
        } catch (error) {
            console.error('Error al cargar la previsualización de la marca de agua:', error);
            setContractWatermarkPreviewUrl(null);
        }
    };

    const memoizedInitialData = useMemo(() => {
        if (!data?.contractTemplate) return undefined;
        try {
            return JSON.parse(data.contractTemplate);
        } catch {
            if (isValidHTML(data.contractTemplate)) {
                return convertHTMLToEditorJSON(data.contractTemplate);
            }
            return undefined;
        }
    }, [data?.contractTemplate]);

    const loadVariables = async () => {
        try {
            const response = await (new ProductService()).getContractFields();
            const responseData = response.getResponseData()?.data;
            responseData && setVariables(responseData || []);
        } catch (error) {
            console.error('Error al cargar las variables:', error);
            toast.error('Error al cargar las variables');
        }
    };

    const handleClose = () => {
        formik.resetForm();
        setContractWatermarkFile(null);
        setContractWatermarkPreviewUrl(null);
        setRemoveContractWatermark(false);
        onClose();
    };

    const create = async (values: any) => {
        const payload = {
            name: values.name,
            description: values.description || '',
            renewalSettings: values.renewalSettings || null,
            renewalDaysNumber: values.renewalSettings === 'custom_period' ? values.renewalDaysNumber : null,
            contractTemplate: values.contractTemplate ?? null,
            consultancyFieldsRequired: values.consultancyFieldsRequired ?? false,
            templateConceptClientInvoice: values.templateConceptClientInvoice || null,
            templateConceptTutorInvoice: values.templateConceptTutorInvoice || null,
            templateConceptInternalInvoice: values.templateConceptInternalInvoice || null,
        };
        let success = false;
        try {
            const response = await productTypeService.createProductType(payload);
            const responseData = response.getResponseData();
            success = responseData.success;
            if (responseData.success) {
                const createdProductTypeId = responseData.data?.id;
                if (createdProductTypeId && contractWatermarkFile) {
                    const uploadResponse = await (
                        await productTypeService.updateContractWatermark(createdProductTypeId, contractWatermarkFile, false)
                    ).getResponseData();

                    if (!uploadResponse.success) {
                        toast.error(uploadResponse.message ?? 'El tipo de producto se ha creado, pero no se ha podido guardar la marca de agua');
                    }
                }

                toast.success('Tipo de producto creado correctamente');
                formik.resetForm();
                refetch();
                onClose();
            } else {
                responseData.data?.errors?.forEach((error: any) => {
                    toast.error(formatValidationError(error, PRODUCT_TYPE_FIELD_LABELS));
                });
            }
        } catch (error: any) {
            toast.error(error?.message ?? 'Error al crear el tipo de producto');
        } finally {
            if (success) handleClose();
        }
    };

    const edit = async (values: any) => {
        const payload = {
            productTypeId: data?.productTypeId,
            name: values.name,
            description: values.description || '',
            renewalSettings: values.renewalSettings || null,
            renewalDaysNumber: values.renewalSettings === 'custom_period' ? values.renewalDaysNumber : null,
            contractTemplate: values.contractTemplate ?? null,
            consultancyFieldsRequired: values.consultancyFieldsRequired ?? false,
            templateConceptClientInvoice: values.templateConceptClientInvoice || null,
            templateConceptTutorInvoice: values.templateConceptTutorInvoice || null,
            templateConceptInternalInvoice: values.templateConceptInternalInvoice || null,
        };
        let success = false;
        try {
            const response = await productTypeService.editProductType(payload);
            const responseData = response.getResponseData();
            success = responseData.success;
            if (responseData.success) {
                if (data?.productTypeId && (contractWatermarkFile || removeContractWatermark)) {
                    const uploadResponse = await (
                        await productTypeService.updateContractWatermark(
                            data.productTypeId,
                            contractWatermarkFile,
                            removeContractWatermark
                        )
                    ).getResponseData();

                    if (!uploadResponse.success) {
                        toast.error(uploadResponse.message ?? 'El tipo de producto se ha actualizado, pero no se ha podido guardar la marca de agua');
                    }
                }

                toast.success('Tipo de producto actualizado correctamente');
                formik.resetForm();
                refetch();
                onClose();
            } else {
                responseData.data?.errors?.forEach((error: any) => {
                    toast.error(formatValidationError(error, PRODUCT_TYPE_FIELD_LABELS));
                });
            }
        } catch (error: any) {
            toast.error(error?.message ?? 'Error al editar el tipo de producto');
        } finally {
            if (success) handleClose();
        }
    };

    const formik = useFormik<ProductTypeFormData>({
        initialValues: {
            name: '',
            description: '',
            renewalSettings: null,
            renewalDaysNumber: null,
            contractTemplate: null,
            consultancyFieldsRequired: false,
            templateConceptClientInvoice: '',
            templateConceptTutorInvoice: '',
            templateConceptInternalInvoice: '',
        },
        validationSchema: schema,
        validateOnBlur: false,
        onSubmit: async (values) => {
            if (mode === 'Editar') {
                await edit(values);
            } else {
                await create(values);
            }
        },
    });

    const currentTemplateProductType = useMemo(() => ({
        id: data?.productTypeId ?? null,
        name: formik.values.name ?? data?.name ?? null,
    }), [data?.name, data?.productTypeId, formik.values.name]);

    useEffect(() => {
        if (isOpen) {
            loadVariables();
        }
    }, [isOpen]);

    useEffect(() => {
        if (data && isOpen) {
            formik.setValues({
                name: data.name || '',
                description: data.description || '',
                renewalSettings: data.renewalSettings || '',
                renewalDaysNumber: data.renewalDaysNumber || null,
                contractTemplate: data.contractTemplate ?? null,
                consultancyFieldsRequired: parseBooleanValue(data.consultancyFieldsRequired ?? false),
                templateConceptClientInvoice: data.templateConceptClientInvoice ?? '',
                templateConceptTutorInvoice: data.templateConceptTutorInvoice ?? '',
                templateConceptInternalInvoice: data.templateConceptInternalInvoice ?? '',
            });

            setContractWatermarkFile(null);
            setRemoveContractWatermark(false);
            if (data.contractWatermarkDocument?.id) {
                loadDocumentPreview(data.contractWatermarkDocument.id);
            } else {
                setContractWatermarkPreviewUrl(null);
            }
        }
    }, [data, isOpen]);

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="xl">
            <div className="m-auto px-7 pt-6 pb-8 max-h-[85vh] overflow-y-auto">
                <div className="mb-7 flex items-center justify-between">
                    <Text className="text-lg font-semibold text-gray-900">
                        {mode + ' tipo de producto'}
                    </Text>
                    <ActionIcon
                        size="sm"
                        variant="text"
                        onClick={handleClose}
                        className="p-0 text-gray-500 hover:!text-gray-900"
                    >
                        <IoClose className="h-[18px] w-[18px]" />
                    </ActionIcon>
                </div>

                <form onSubmit={formik.handleSubmit} className="grid grid-cols-1 gap-4">
                    <RequiredInput
                        id='name'
                        label="Nombre"
                        formik={formik}
                    />

                    <Textarea
                        id="description"
                        label="Descripción"
                        rows={3}
                        value={formik.values.description || ''}
                        onChange={formik.handleChange}
                        error={formik.errors.description}
                    />

                    <Select
                        label="Configuración de renovación"
                        placeholder="Seleccione la configuración de renovación"
                        options={renewalSettingsOptions}
                        value={renewalSettingsOptions.find(option => option.value === formik.values.renewalSettings) || []}
                        onChange={(selectedOption: any) => {
                            formik.setFieldValue('renewalSettings', selectedOption?.value || null);
                            if (selectedOption?.value !== 'custom_period') {
                                formik.setFieldValue('renewalDaysNumber', null);
                            }
                        }}
                        error={formik.errors.renewalSettings}
                    />

                    {formik.values.renewalSettings === 'custom_period' && (
                        <RequiredInput
                            id='renewalDaysNumber'
                            label="Días antes de la renovación"
                            type="number"
                            formik={formik}
                        />
                    )}

                    <Switch
                        id='consultancyFieldsRequired'
                        label="Necesarios campos de asesoría"
                        checked={formik.values.consultancyFieldsRequired}
                        onChange={() => formik.setFieldValue('consultancyFieldsRequired', !formik.values.consultancyFieldsRequired)}
                        switchKnobClassName='bg-secondary'
                    />

                    <FormGroup
                        title="Plantilla de Contrato"
                        description="Plantilla por defecto para productos de este tipo. Los productos pueden definir su propia plantilla que tendrá prioridad."
                        className='pt-6'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10"
                    >
                        <div className="col-span-full space-y-4">
                            <div className="space-y-3 rounded-md border border-gray-200 p-4">
                                <div className="space-y-1">
                                    <Text className="text-sm font-medium text-gray-900">Marca de agua</Text>
                                    <Text className="text-xs text-gray-500">
                                        Si se configura, se aplicará en cada hoja del contrato y en la previsualización.
                                    </Text>
                                </div>

                                <FileInput
                                    accept="image/*"
                                    placeholder="Seleccionar imagen de marca de agua"
                                    onChange={(event: React.ChangeEvent<any>) => {
                                        const file = event.target.files?.[0];
                                        if (!file) {
                                            return;
                                        }

                                        setContractWatermarkFile(file);
                                        setRemoveContractWatermark(false);
                                        setContractWatermarkPreviewUrl(URL.createObjectURL(file));
                                        event.target.value = '';
                                    }}
                                />

                                {contractWatermarkPreviewUrl && (
                                    <div className="space-y-3">
                                        <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-4">
                                            <img
                                                src={contractWatermarkPreviewUrl}
                                                alt="Previsualización de marca de agua"
                                                className="mx-auto max-h-40 w-auto object-contain opacity-70"
                                            />
                                        </div>

                                        <Button
                                            type="button"
                                            variant="outline"
                                            color="danger"
                                            onClick={() => {
                                                setContractWatermarkFile(null);
                                                setContractWatermarkPreviewUrl(null);
                                                setRemoveContractWatermark(true);
                                            }}
                                        >
                                            Quitar marca de agua
                                        </Button>
                                    </div>
                                )}
                            </div>

                            <ContractTemplateWYSIWYGEditor
                                type='contract'
                                initialData={memoizedInitialData}
                                onChange={(editorData) => formik.setFieldValue('contractTemplate', convertEditorJSONToHTML(editorData))}
                                placeholder="Maqueta aquí la plantilla de contrato para este tipo de producto..."
                                variables={variables}
                                productType={currentTemplateProductType}
                                watermarkImage={contractWatermarkPreviewUrl}
                            />
                        </div>
                    </FormGroup>

                    <FormGroup
                        title="Facturación"
                        description="Define las plantillas de concepto de factura para este tipo de producto."
                        className='pt-6'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10"
                    >
                        <InvoiceConceptBillingSection
                            idPrefix={productTypeInputId}
                            values={{
                                templateConceptClientInvoice: formik.values.templateConceptClientInvoice,
                                templateConceptTutorInvoice: formik.values.templateConceptTutorInvoice,
                                templateConceptInternalInvoice: formik.values.templateConceptInternalInvoice,
                            }}
                            errors={{
                                templateConceptClientInvoice: formik.errors.templateConceptClientInvoice,
                                templateConceptTutorInvoice: formik.errors.templateConceptTutorInvoice,
                                templateConceptInternalInvoice: formik.errors.templateConceptInternalInvoice,
                            }}
                            onChange={(field, value) => formik.setFieldValue(field, value)}
                        />
                    </FormGroup>

                    <div className="flex items-center justify-end gap-4 pt-4">
                        <Button
                            variant="outline"
                            onClick={handleClose}
                            className="w-full @xl:w-auto"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            isLoading={formik.isSubmitting}
                            className="w-full @xl:w-auto"
                        >
                            {mode}
                        </Button>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default ProductTypeModalForm;
